import { Controller } from "stimulus";
import { Modal } from "bootstrap";

const SIDEBAR_SCROLL_KEY = "staffScheduleSidebarScroll";
const DETAIL_SCROLL_KEY = "staffScheduleDetailScroll";
const SEARCH_TEXT_KEY = "staffScheduleSearchText";
const CHIP_COLOR = /^#[0-9A-Fa-f]{6}$/;

export default class extends Controller {
  static targets = [
    "searchInput",
    "facilitatorRow",
    "filterEmpty",
    "visibleCount",
    "sidebarList",
    "detailPanel",
    "bookingModal",
    "bookingCard",
    "bookingName",
    "bookingWith",
    "bookingDetails",
    "bookingActions",
    "bookingEventLink",
  ];

  connect() {
    this.beforeVisitHandler = this.prepareForVisit.bind(this);
    document.addEventListener("turbolinks:before-visit", this.beforeVisitHandler);
    document.addEventListener("turbolinks:before-cache", this.beforeVisitHandler);

    this.restoreSearchText();
    this.filterRows();
    this.restoreScrollPositions();
  }

  disconnect() {
    document.removeEventListener("turbolinks:before-visit", this.beforeVisitHandler);
    document.removeEventListener("turbolinks:before-cache", this.beforeVisitHandler);
    if (this.bookingModal) {
      this.bookingModal.dispose();
      this.bookingModal = null;
    }
  }

  prepareForVisit() {
    this.dismissBookingModal();
    this.saveScrollPositions();
  }

  openBooking(event) {
    event.preventDefault();
    const modal = this.bookingModalInstance();
    if (!modal) return;

    const params = event.params;
    this.bookingTrigger = event.target.closest("button");
    this.bookingNameTarget.textContent = params.name || "";
    this.bookingWithTarget.textContent = params.withLabel || "";
    this.bookingWithTarget.classList.toggle(
      "rbs-cal-modal__with--alone",
      params.alone === true || params.alone === "true"
    );

    const color = String(params.color || "");
    this.bookingCardTarget.style.borderLeftColor = CHIP_COLOR.test(color)
      ? color
      : "#64748b";

    this.bookingDetailsTarget.textContent = "";
    this.appendBookingDetail("Date", params.date);
    this.appendBookingDetail("Time", params.time);
    this.appendBookingDetail("Location", params.location);
    this.appendBookingDetail(params.roleLabel || "Role", params.roles);
    this.appendBookingDetail(params.roomLabel || "Room", params.rooms);

    if (params.materialsUrl) {
      this.bookingEventLinkTarget.href = params.materialsUrl;
      const eventName = params.name || "this event";
      this.bookingEventLinkTarget.setAttribute(
        "aria-label",
        `View event information for ${eventName}`
      );
      this.bookingActionsTarget.hidden = false;
    } else {
      this.bookingEventLinkTarget.removeAttribute("href");
      this.bookingActionsTarget.hidden = true;
    }

    modal.show();
  }

  closeBooking(event) {
    if (event) event.preventDefault();
    if (this.bookingModal) this.bookingModal.hide();
  }

  appendBookingDetail(label, value) {
    if (!value) return;

    const row = document.createElement("div");
    row.className = "rb-detail";

    const labelEl = document.createElement("span");
    labelEl.className = "rb-detail__label";
    labelEl.textContent = label;

    const valueEl = document.createElement("span");
    valueEl.className = "rb-detail__value";
    valueEl.textContent = value;

    row.appendChild(labelEl);
    row.appendChild(valueEl);
    this.bookingDetailsTarget.appendChild(row);
  }

  bookingModalInstance() {
    if (!this.hasBookingModalTarget) return null;
    if (this.bookingModal) return this.bookingModal;

    this.bookingModal = new Modal(this.bookingModalTarget);
    this.bookingModalTarget.addEventListener("hidden.bs.modal", () => {
      if (this.bookingTrigger && document.body.contains(this.bookingTrigger)) {
        this.bookingTrigger.focus();
      }
    });
    return this.bookingModal;
  }

  dismissBookingModal() {
    if (this.bookingModal) this.bookingModal.hide();
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("padding-right");
    document.querySelectorAll(".modal-backdrop").forEach((element) => {
      element.remove();
    });
  }

  filterRows() {
    const query = this.hasSearchInputTarget
      ? this.searchInputTarget.value.trim().toLowerCase()
      : "";
    let visibleCount = 0;

    this.facilitatorRowTargets.forEach((row) => {
      const haystack = row.dataset.searchText || "";
      const visible = !query || haystack.includes(query);

      row.classList.toggle("hidden", !visible);
      if (visible) visibleCount += 1;
    });

    if (this.hasFilterEmptyTarget) {
      this.filterEmptyTarget.classList.toggle("hidden", visibleCount > 0);
    }

    if (this.hasVisibleCountTarget) {
      this.visibleCountTarget.textContent = String(visibleCount);
    }
  }

  searchChanged() {
    this.persistSearchText();
    this.filterRows();
  }

  restoreSearchText() {
    if (!this.hasSearchInputTarget) return;

    const saved = sessionStorage.getItem(SEARCH_TEXT_KEY);
    if (saved !== null) {
      this.searchInputTarget.value = saved;
    }
  }

  persistSearchText() {
    if (!this.hasSearchInputTarget) return;

    sessionStorage.setItem(SEARCH_TEXT_KEY, this.searchInputTarget.value);
  }

  saveScrollPositions() {
    this.persistSearchText();
    if (this.hasSidebarListTarget) {
      sessionStorage.setItem(
        SIDEBAR_SCROLL_KEY,
        String(this.sidebarListTarget.scrollTop)
      );
    }

    if (this.hasDetailPanelTarget) {
      sessionStorage.setItem(
        DETAIL_SCROLL_KEY,
        String(this.detailPanelTarget.scrollTop)
      );
    }
  }

  restoreScrollPositions() {
    if (this.hasSidebarListTarget) {
      const savedSidebar = sessionStorage.getItem(SIDEBAR_SCROLL_KEY);
      if (savedSidebar !== null) {
        this.sidebarListTarget.scrollTop = parseInt(savedSidebar, 10) || 0;
      }
    }

    if (this.hasDetailPanelTarget) {
      const switchingFacilitator = sessionStorage.getItem("staffScheduleSwitch") === "true";
      sessionStorage.removeItem("staffScheduleSwitch");

      if (switchingFacilitator) {
        this.detailPanelTarget.scrollTop = 0;
        sessionStorage.removeItem(DETAIL_SCROLL_KEY);
      } else {
        const savedDetail = sessionStorage.getItem(DETAIL_SCROLL_KEY);
        if (savedDetail !== null) {
          this.detailPanelTarget.scrollTop = parseInt(savedDetail, 10) || 0;
        }
      }
    }
  }

  facilitatorSelected() {
    sessionStorage.setItem("staffScheduleSwitch", "true");
    this.saveScrollPositions();
  }

  monthChanged(event) {
    const url = event.target.value;
    if (!url) return;

    if (window.Turbolinks && typeof window.Turbolinks.visit === "function") {
      window.Turbolinks.visit(url);
    } else {
      window.location.assign(url);
    }
  }
}
